import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Package,
  Search,
  Filter,
  QrCode,
  CheckCircle,
  Clock,
  AlertTriangle,
  Truck,
  Box,
  Eye,
  Calendar,
  User,
  MapPin,
  Plus,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';
import { Project, ProjectCarton, CartonFilter, CartonReceivingRequest } from '../types';
import { apiService } from '../services/api';
import { useAuth, useProject } from '../contexts';
import { toast } from 'react-hot-toast';
import Scanner from '../components/Scanner';
import LoadingSpinner from '../components/LoadingSpinner';
import { cartonCacheService } from '../services/cartonCacheService';
import Button from '../components/Button';
import { ProjectSelector } from '../components/ProjectSelector';

const CartonsContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing[6]};
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography['3xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing[2]} 0 0 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme, variant }) => variant === 'primary' ? theme.colors.primary : theme.colors.white};
  color: ${({ theme, variant }) => variant === 'primary' ? theme.colors.white : theme.colors.text};
  border: 1px solid ${({ theme, variant }) => variant === 'primary' ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme, variant }) => variant === 'primary' ? theme.colors.primaryDark : theme.colors.gray50};
    transform: translateY(-1px);
  }
`;

const FiltersCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};
  align-items: end;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing[3]};
  top: 70%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[12]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.base};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.base};
  background: ${({ theme }) => theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography['2xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const CartonsList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const CartonCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const CartonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const CartonInfo = styled.div`
  flex: 1;
`;

const CartonCode = styled.h3`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const CartonProject = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: ${({ theme }) => theme.typography.medium};
  text-transform: capitalize;
  background: ${({ theme, status }) => {
    switch (status) {
      case 'packed': return theme.colors.warningBackground;
      case 'dispatched': return theme.colors.primaryBackground;
      case 'received': return theme.colors.successBackground;
      case 'partial': return theme.colors.warningBackground;
      case 'missing': return theme.colors.errorBackground;
      case 'damaged': return theme.colors.errorBackground;
      default: return theme.colors.gray100;
    }
  }};
  color: ${({ theme, status }) => {
    switch (status) {
      case 'packed': return theme.colors.warning;
      case 'dispatched': return theme.colors.primary;
      case 'received': return theme.colors.success;
      case 'partial': return theme.colors.warning;
      case 'missing': return theme.colors.error;
      case 'damaged': return theme.colors.error;
      default: return theme.colors.textSecondary;
    }
  }};
`;

const CartonDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.gray100};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorContainer = styled.div`
  color: ${({ theme }) => theme.colors.error};
  text-align: center;
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin: ${({ theme }) => theme.spacing[4]} 0;
`;

const RetryButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  margin-top: ${({ theme }) => theme.spacing[4]};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const ProjectInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
`;

const ProjectBadge = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    transform: translateY(-1px);
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
  cursor: pointer;
  background: none;
  border: none;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[12]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.base};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const SearchInputIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing[3]};
  top: 70%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CartonList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const PaginationButton = styled.button`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.base};
  font-weight: ${({ theme }) => theme.typography.medium};
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  font-size: ${({ theme }) => theme.typography.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 ${({ theme }) => theme.spacing[4]};
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const ProjectTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const ProjectSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing[1]} 0 0 0;
`;

const SwitchButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const ProjectStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const CartonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const CartonsPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedProject, setSelectedProject, loading: projectContextLoading } = useProject();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [cartons, setCartons] = useState<ProjectCarton[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [scannerMode, setScannerMode] = useState<'receive' | 'dispatch' | 'content'>('receive');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [projectData, setProjectData] = useState<any>(null);
  const [showProjectSelector, setShowProjectSelector] = useState(false);

  // Load projects and project data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectsData] = await Promise.all([
          apiService.getProjects()
        ]);
        setProjects(projectsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Load project details when selected
  useEffect(() => {
    console.log('CartonsPage: selectedProject changed:', selectedProject);
    const loadProjectData = async () => {
      if (!selectedProject) {
        console.log('CartonsPage: No selected project, clearing data');
        setProjectData(null);
        setCartons([]);
        setError(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Use the same efficient pattern as CartonContentReceivingPage
        const projectData = await apiService.getProject(selectedProject.id);
        
        if (projectData) {
          console.log('CartonsPage: Project data loaded:', projectData);
          setProjectData(projectData);
          
          // Extract cartons from project data if available
          if (projectData.cartons && Array.isArray(projectData.cartons)) {
            setCartons(projectData.cartons);
            console.log('CartonsPage: Cartons loaded from project data:', projectData.cartons.length);
          } else {
            // Fallback: Load cartons separately if not in project data
            try {
              const cartonsData = await apiService.getProjectCartons(selectedProject.id);
              setCartons(cartonsData);
              console.log('CartonsPage: Cartons loaded separately:', cartonsData.length);
            } catch (cartonsError) {
              console.warn('CartonsPage: Failed to load cartons separately:', cartonsError);
              setCartons([]);
            }
          }
        } else {
          throw new Error('Invalid project data received');
        }
      } catch (error) {
        console.error('Unexpected error loading project data:', error);
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
  }, [selectedProject]);

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setShowProjectSelector(false);
  };

  const handleCartonClick = (cartonId: string) => {
    navigate(`/cartons/${cartonId}`);
  };

  const handleScan = async (barcode: string) => {
    if (!selectedProject) {
      toast.error('Please select a project first');
      return;
    }

    try {
      if (scannerMode === 'receive') {
        const request: CartonReceivingRequest = {
          received_by: Number(user?.id) || 0,
          remarks: 'Received via mobile app',
          project_id: selectedProject.id
        };
        await apiService.receiveCarton(barcode, request);
        toast.success('Carton received successfully');
      } else if (scannerMode === 'dispatch') {
        await apiService.updateCartonStatus(
          barcode, 
          'dispatched', 
          'Dispatched via mobile app',
          selectedProject.id
        );
        toast.success('Carton dispatched successfully');
      } else if (scannerMode === 'content') {
        // Handle content scanning
        navigate(`/cartons/content/${barcode}`);
        return;
      }
      
      // Refresh cartons list
      if (selectedProject) {
        const apiCartons = await apiService.getProjectCartons(selectedProject.id);
        setCartons(apiCartons);
      }
    } catch (error) {
      console.error('Error processing carton:', error);
      toast.error(`Failed to ${scannerMode} carton`);
    } finally {
      setShowScanner(false);
    }
  };

  const openScanner = (mode: 'receive' | 'dispatch' | 'content') => {
    if (!selectedProject) {
      toast.error('Please select a project first');
      setShowProjectSelector(true);
      return;
    }
    setScannerMode(mode);
    setShowScanner(true);
  };

  const filteredCartons = (cartons || []).filter(carton => 
    !searchQuery || carton.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalCartons = filteredCartons.length;
  const totalPanels = filteredCartons.reduce((sum, carton) => sum + (carton.panels?.length || 0), 0);
  const totalHardware = filteredCartons.reduce((sum, carton) => sum + (carton.hardware?.length || 0), 0);

  const totalPages = Math.ceil(filteredCartons.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredCartons.length);

  // Show loading if project context is still loading or if we're loading project data
  if (projectContextLoading || (loading && !projectData)) {
    return (
      <CartonsContainer>
        <Header>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Back
          </BackButton>
          <Title>Carton Management</Title>
          <Subtitle>Loading project details...</Subtitle>
        </Header>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          gap: '16px'
        }}>
          <LoadingSpinner />
          <p>{projectContextLoading ? 'Loading project context...' : 'Loading project details and cartons...'}</p>
        </div>
      </CartonsContainer>
    );
  }

  if (error) {
    return (
      <CartonsContainer>
        <Header>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Back
          </BackButton>
          <Title>Carton Management</Title>
          <Subtitle>Error loading data</Subtitle>
        </Header>
        <ErrorContainer>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: '#ef4444', marginBottom: '8px' }}>Unable to Load Cartons</h3>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>{error}</p>
          </div>
          <RetryButton onClick={() => window.location.reload()}>
            <RefreshCw size={16} />
            Retry
          </RetryButton>
        </ErrorContainer>
      </CartonsContainer>
    );
  }

  return (
    <CartonsContainer>
      <Header>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Back
        </BackButton>
        <Title>Carton Management</Title>
        <Subtitle>View and manage all cartons in the system</Subtitle>
      </Header>

      {showProjectSelector && (
        <ProjectSelector
          projects={projects}
          onSelect={handleProjectSelect}
          onClose={() => setShowProjectSelector(false)}
        />
      )}

      {selectedProject ? (
        <>
          <ProjectHeader>
            <div>
              <ProjectTitle>
                {projectData?.project?.display_name || selectedProject.name}
              </ProjectTitle>
              <ProjectSubtitle>
                {projectData?.project?.customer_name || 'Loading project details...'}
                {projectData?.project?.so_number && ` • SO: ${projectData.project.so_number}`}
              </ProjectSubtitle>
            </div>
            <SwitchButton onClick={() => setShowProjectSelector(true)}>
              Switch Project
            </SwitchButton>
          </ProjectHeader>

          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <p>Loading project data...</p>
            </EmptyState>
          ) : projectData ? (
            <>
              <ProjectStats>
                <StatsGrid>
                  <StatCard>
                    <StatValue>
                      {projectData?.statistics?.cartons?.total_cartons || 
                       (cartons || []).length}
                    </StatValue>
                    <StatLabel>Total Cartons</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>
                      {projectData?.statistics?.panels?.total_panels || 
                       (projectData?.panels?.length || 0) || 
                       (cartons || []).reduce((sum, c) => sum + (c.panel_items || c.panels?.length || 0), 0)}
                    </StatValue>
                    <StatLabel>Total Panels</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>
                      {projectData?.statistics?.hardware?.total_hardware || 
                       (projectData?.hardware?.length || 0) || 
                       (cartons || []).reduce((sum, c) => sum + (c.hw_items || c.hardware?.length || 0), 0)}
                    </StatValue>
                    <StatLabel>Total Hardware</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>
                      {selectedProject?.status || 'Active'}
                    </StatValue>
                    <StatLabel>Status</StatLabel>
                  </StatCard>
                </StatsGrid>
              </ProjectStats>

              <Actions>
                <SearchInputWrapper>
                  <SearchInputIcon>
                    <Search size={18} />
                  </SearchInputIcon>
                  <SearchInput
                    type="text"
                    placeholder="Search cartons..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </SearchInputWrapper>

                <ActionButton 
                  variant="primary" 
                  onClick={() => openScanner('receive')}
                >
                  <CheckCircle size={18} />
                  Receive Carton
                </ActionButton>
                <ActionButton 
                  variant="secondary" 
                  onClick={() => openScanner('dispatch')}
                >
                  <Truck size={18} />
                  Dispatch
                </ActionButton>
                <ActionButton 
                  variant="secondary" 
                  onClick={() => openScanner('content')}
                >
                  <Box size={18} />
                  Contents
                </ActionButton>
              </Actions>

              {filteredCartons.length > 0 ? (
                <CartonGrid>
                  {filteredCartons.slice(startIndex, endIndex).map((carton) => (
                    <CartonCard 
                      key={carton.id} 
                      onClick={() => handleCartonClick(carton.id)}
                    >
                      <CartonHeader>
                        <div>Carton {carton.code}</div>
                        <div>
                          {carton.status === 'received' ? (
                            <CheckCircle color="green" size={20} />
                          ) : carton.status === 'dispatched' ? (
                            <Truck color="blue" size={20} />
                          ) : (
                            <AlertTriangle color="orange" size={20} />
                          )}
                        </div>
                      </CartonHeader>
                      <CartonDetails>
                        <DetailItem>
                          <Box size={16} />
                          <span>{carton.panel_items || carton.panels?.length || 0} panels</span>
                        </DetailItem>
                        <DetailItem>
                          <Package size={16} />
                          <span>{carton.hw_items || carton.hardware?.length || 0} items</span>
                        </DetailItem>
                        <DetailItem>
                          <Calendar size={16} />
                          <span>
                            {carton.received_at 
                              ? `Received ${new Date(carton.received_at).toLocaleDateString()}`
                              : 'Not received yet'}
                          </span>
                        </DetailItem>
                        {carton.status === 'dispatched' && carton.dispatched_at && (
                          <DetailItem>
                            <Truck size={16} />
                            <span>Dispatched {new Date(carton.dispatched_at).toLocaleDateString()}</span>
                          </DetailItem>
                        )}
                      </CartonDetails>
                    </CartonCard>
                  ))}
                </CartonGrid>
              ) : (
                <EmptyState>
                  <Package size={48} />
                  <h3>No Cartons Found</h3>
                  <p>No cartons match your search criteria.</p>
                  <Button onClick={() => setSearchQuery('')}>
                    Clear Search
                  </Button>
                </EmptyState>
              )}

              {filteredCartons.length > itemsPerPage && (
                <Pagination>
                  <PaginationButton 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </PaginationButton>
                  <PageInfo>
                    Page {currentPage} of {totalPages}
                  </PageInfo>
                  <PaginationButton 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </PaginationButton>
                </Pagination>
              )}
            </>
          ) : (
            <EmptyState>
              <Package size={48} />
              <h3>No Project Selected</h3>
              <p>Please select a project to view cartons</p>
              <Button onClick={() => setShowProjectSelector(true)}>
                Select Project
              </Button>
            </EmptyState>
          )}
        </>
      ) : (
        <EmptyState>
          <Package size={48} />
          <h3>No Project Selected</h3>
          <p>Please select a project to view cartons</p>
          <Button onClick={() => setShowProjectSelector(true)}>
            Select Project
          </Button>
        </EmptyState>
      )}

      {showScanner && (
        <Scanner
          onClose={() => setShowScanner(false)}
          onScan={handleScan}
          title={`${scannerMode === 'receive' ? 'Receive' : scannerMode === 'dispatch' ? 'Dispatch' : 'View Contents of'} Carton`}
        />
      )}
    </CartonsContainer>
  );
};

export default CartonsPage;